'use client'

import { cn } from '@morpho-org/ui-v6/utils'
import { formatters } from '@morpho-org/ui-v6/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui'
import { cva, type VariantProps } from 'class-variance-authority'
import { forwardRef, type HTMLAttributes } from 'react'

/**
 * PercentageBadge variants using cva for Tailwind class management.
 *
 * Matches v5 PercentageBadge behavior:
 * - Background: valid (green) for positive, alert (red) for negative
 * - Border radius: 4px
 * - Padding: 2px 4px
 * - Typography: body xxxxs regular
 */
const percentageBadgeVariants = cva(
  // Base styles
  [
    'rounded-[4px]',
    'px-2xs',
    'py-[2px]',
    'h-[18px]',
    'inline-flex',
    'justify-center',
    'items-center',
    'whitespace-nowrap'
  ].join(' '),
  {
    variants: {
      isPositive: {
        true: 'bg-success-foreground/24',
        false: 'bg-destructive-foreground/18'
      }
    },
    defaultVariants: {
      isPositive: true
    }
  }
)

const percentageBadgeTextVariants = cva(
  // Base text styles
  'text-3xs',
  {
    variants: {
      isPositive: {
        true: 'text-success-foreground',
        false: 'text-destructive-foreground'
      }
    },
    defaultVariants: {
      isPositive: true
    }
  }
)

export interface PercentageBadgeProps
  extends
    Omit<HTMLAttributes<HTMLDivElement>, 'children'>,
    VariantProps<typeof percentageBadgeVariants> {
  /** The percentage value (e.g., 0.05 for 5%) */
  percentage: number
  /** Content to display in the tooltip */
  tooltipContent?: string
  /** Whether to disable the tooltip */
  disableTooltip?: boolean
}

/**
 * PercentageBadge component - Tailwind replica of dapp-ui-v5 PercentageBadge
 *
 * Features:
 * - Displays a formatted percentage with sign (+/-)
 * - Green background for positive values, red for negative
 * - Compact pill-shaped badge
 * - Optional tooltip support
 *
 * @example
 * ```tsx
 * // Positive percentage
 * <PercentageBadge percentage={0.0523} />
 * // Displays: "+5.23%" with green background
 *
 * // Negative percentage
 * <PercentageBadge percentage={-0.0312} />
 * // Displays: "-3.12%" with red background
 *
 * // With tooltip
 * <PercentageBadge percentage={0.05} tooltipContent="Performance over 24h" />
 * ```
 */
const PercentageBadge = forwardRef<HTMLDivElement, PercentageBadgeProps>(
  ({ className, percentage, tooltipContent, disableTooltip, ...props }, ref) => {
    const isPositive = percentage > 0

    const percentageText = formatters.percent.digits(2).unit('%').sign().of(percentage)

    const badge = (
      <div ref={ref} className={cn(percentageBadgeVariants({ isPositive }), className)} {...props}>
        <span className={percentageBadgeTextVariants({ isPositive })}>{percentageText}</span>
      </div>
    )

    return (
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        {!(!tooltipContent || disableTooltip) && <TooltipContent>{tooltipContent}</TooltipContent>}
      </Tooltip>
    )
  }
)
PercentageBadge.displayName = 'PercentageBadge'

export { PercentageBadge, percentageBadgeVariants, percentageBadgeTextVariants }
